import { DataTable } from "./DataTable/DataTable";
import { Plus, Edit3, Trash2, Eye, Search } from "lucide-react";
import { useState, useEffect } from "react";
import avatarPlaceholder from "../assets/images/default_avatar.jpg";
import { DoctorFilters } from "./Filters/DoctorFilters";
import toast from "react-hot-toast";
import { DoctorService } from "../services/api/doctor.service";
import { AddNewDoctorModal } from "../shared/AddNewDoctor";
import { DoctorDetails } from "./doctors/DoctorDetails";
import { useAuth } from "../context/AuthContext";
import { MedicalCampService } from "../services/api/medicalCamp.Service";
import { DoctorDetailsType } from "../types/doctor";

interface DoctorViewModel extends DoctorDetailsType {
  id: string;
}

export function DoctorsView() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<{ specialization?: string }>({});
  const [doctorsList, setDoctorList] = useState<DoctorViewModel[]>([]);
  const [ShowAddDoctor, setShowAddDoctor] = useState(false);
  const [showDoctorDetails, setShowDoctorDetails] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorViewModel | null>(
    null,
  );
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [previousPage, setPreviousPage] = useState<number | null>(null);

  const handleEditClick = (doctor: DoctorDetailsType) => {
    const doctorViewModel: DoctorViewModel = {
      ...doctor,
      id: (doctor as any).id || doctor.userId,
    };
    setSelectedDoctor(doctorViewModel);
    setShowAddDoctor(true);
  };

  const handleFilterChange = (newFilter: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilter,
    }));
  };

  const filteredDoctors = doctorsList.filter((doctor) => {
    if (filters.specialization) {
      return doctor.specialization.some(
        (spec) => spec.toLowerCase() === filters.specialization,
      );
    }
    return true;
  });

  // useEffect(()=>{
  //   loadDoctors(1,10,searchText,filters.specialization)
  // },[filters.specialization, searchText])

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      console.log(filters);
      try {
        if (
          user?.role.toLocaleLowerCase() === "clinic" ||
          user?.role.toLocaleLowerCase() === "admin"
        ) {
          await loadDoctors(
            currentPage,
            pageSize,
            searchText,
            filters.specialization,
          );
        } else if (user?.role.toLocaleLowerCase() === "medicalcamp") {
          await loadCampDoctors();
        }
      } catch (error) {
        console.error("Error loading doctors:", error);
        toast.error("Failed to load doctors");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, currentPage, pageSize, filters.specialization, searchText]);

  const loadDoctors = async (
    page = 1,
    pageSize = 10,
    searchText = "",
    specialization?: string,
  ) => {
    try {
      setIsLoading(true);
      const response = await DoctorService.getDoctors({
        page,
        pageSize,
        searchText,
        specialization,
      });
      if (response.success && response.data) {
        const mappedList = (response.data.dataList || []).map((doc: any) => ({
          ...doc,
          id: doc.userId,
        }));
        setDoctorList(mappedList);
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setNextPage(response.data.nextPage);
        setPreviousPage(response.data.previousPage);
        setTotalItemsCount(response.data.totalItemsCount);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to load");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCampDoctors = async () => {
    try {
      setIsLoading(true);
      const [campResponse, doctorsResponse] = await Promise.all([
        MedicalCampService.getMedicalCamps({ page: 1, pageSize: 10 }),
        DoctorService.getDoctors({ page: 1, pageSize: 100 }),
      ]);

      if (!campResponse.success || !doctorsResponse.success) {
        toast.error("Failed to fetch data");
        return;
      }

      const userCamp = campResponse.data?.dataList.find(
        (camp: any) => camp.username === user?.username,
      );

      if (!userCamp) {
        toast.error("No medical camp assigned to this user");
        return;
      }

      const campDoctors = userCamp.campDoctors || [];

      // Matching campDoctors with full doctor details
      const matchedDoctors = campDoctors
        .map((campDoctor: any) => {
          const doctorDetails = doctorsResponse.data?.dataList.find(
            (doctor: any) => doctor.userId === campDoctor.doctorId, // Assuming list has flat structure now
          ) as DoctorDetailsType | undefined;

          // If the list structure from getDoctors isn't what we expect (if it was doctorDetails), we might need to adjust.
          // But we updated getDoctors to return dataList directly.

          return doctorDetails
            ? {
                ...doctorDetails,
                amount: campDoctor.amount,
                id: doctorDetails.userId,
              }
            : null;
        })
        .filter(Boolean) as DoctorViewModel[];

      setDoctorList(matchedDoctors);
    } catch (error) {
      console.error("Error loading camp doctors:", error);
      toast.error("Failed to load camp doctors");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDoctor = async (
    doctorId: string,
    event: React.MouseEvent,
    status: boolean,
  ) => {
    event.stopPropagation();

    try {
      const response = await DoctorService.markDoctorDecommissiond({
        doctorId: doctorId,
        decommissioned: status,
      });
      console.log("response", response);
      if (response.success) {
        toast.success(`Doctor ${status ? "Disabled" : "Enabled"} Successfully`);
        loadDoctors();
      } else {
        toast.error(response.message || "Failed to delete doctor.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      key: "name",
      title: "Name",
      sortable: true,
      width: "w-[250px]",
      render: (doctor: DoctorViewModel) => (
        <div className="flex items-center">
          <img
            src={
              typeof doctor.profileImage === "string" &&
              doctor.profileImage.trim() !== ""
                ? doctor.profileImage
                : avatarPlaceholder
            }
            alt={doctor.name}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />

          <div>
            <div className="font-medium text-gray-900">{doctor.name}</div>
            <div className="text-sm text-gray-500">
              {doctor.specialization?.join(", ") || ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "specialization",
      title: "Specialization",
      sortable: true,
      width: "w-[200px]",
      render: (doctor: DoctorViewModel) => (
        <div>
          <div className="font-medium">
            {doctor.specialization?.join(", ") || ""}
          </div>
          <div className="text-sm text-gray-500">
            Experience:- {doctor.experience}
          </div>
        </div>
      ),
    },
    {
      key: "address",
      title: "Address",
      sortable: true,
      width: "w-[200px]",
      render: (doctor: DoctorViewModel) => (
        <div>
          {doctor.address}, {doctor.state}, {doctor.city} - {doctor.pincode}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (doctor: DoctorViewModel) => (
        <div className="flex items-center gap-4">
          {/* View Details (Visible to All Users) */}
          <button
            className={`text-blue-600 hover:text-blue-700 ${
              user?.role?.toLocaleLowerCase() === "clinicsupervisor"
                ? "ml-4"
                : ""
            }`}
            onClick={() => {
              setSelectedDoctor(doctor);
              setShowDoctorDetails(true);
            }}
            aria-label={`View details of ${doctor.name}`}
            title={`View details of ${doctor.name}`}
          >
            <Eye className="w-5 h-5" />
          </button>

          {/* Edit (Only for Admin) */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <button
              className="text-green-600 hover:text-green-700"
              onClick={(event) => {
                event.stopPropagation();
                handleEditClick(doctor);
              }}
              title={`Edit details of ${doctor.name}`}
            >
              <Edit3 className="w-5 h-5" />
            </button>
          )}

          {/* Delete (Only for Admin) */}
          {user?.role?.toLocaleLowerCase() === "admin" && (
            <>
              {doctor?.isDecommissioned ? (
                <>
                  <button
                    className="block rounded-full text-green-600 hover:text-green-700 border border-gray-400/30 p-1 px-4 "
                    onClick={(event) =>
                      handleDeleteDoctor(doctor.userId, event, false)
                    }
                    title={`Enabled ${doctor.name}`}
                  >
                    Enable
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="block rounded-full text-red-600 hover:text-red-700 border border-gray-400/30 p-1 px-4"
                    onClick={(event) =>
                      handleDeleteDoctor(doctor.userId, event, true)
                    }
                    title={`Disable ${doctor.name}`}
                  >
                    Disable
                  </button>
                </>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  const addDoctorButton = (
    <button
      onClick={() => setShowAddDoctor(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      Add Doctor
    </button>
  );

  const handleAddDoctorSuccess = () => {
    setShowAddDoctor(false);
    loadDoctors();
  };
  return (
    <div className="relative pb-2">
      {!showDoctorDetails && (
        <div
          className={`flex justify-end ${
            user?.role.toLocaleLowerCase() === "admin"
              ? "relative top-9"
              : user?.role.toLocaleLowerCase() === "clinic"
                ? "top-6"
                : user?.role.toLocaleLowerCase() === "medicalcamp"
                  ? "relative top-9"
                  : null
          }`}
        >
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search doctors..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
            />
          </div>
        </div>
      )}
      {showDoctorDetails && selectedDoctor ? (
        <DoctorDetails
          onClose={() => {
            setShowDoctorDetails(false);
            setSelectedDoctor(null);
          }}
          doctor={selectedDoctor}
          onEdit={handleEditClick}
        />
      ) : (
        <DataTable
          data={doctorsList}
          columns={columns}
          itemsPerPage={pageSize}
          isLoading={isLoading}
          onRowClick={(doctor) => {
            setSelectedDoctor(doctor);
            setShowDoctorDetails(true);
          }}
          customFilters={
            user?.role?.toLocaleLowerCase() === "admin" ? addDoctorButton : null
          }
          filterComponent={
            <DoctorFilters onFilterChange={handleFilterChange} />
          }
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            pageSize,
            setPageSize,
          }}
        />
      )}

      {ShowAddDoctor && (
        <AddNewDoctorModal
          onClose={() => {
            setShowAddDoctor(false);
            setSelectedDoctor(null);
          }}
          onSuccess={handleAddDoctorSuccess}
          doctorId={selectedDoctor?.userId}
        />
      )}
    </div>
  );
}
